import PropTypes from 'prop-types'
import { WarningOutlined } from '@ant-design/icons'
import { createUseStyles, useTheme } from 'react-jss'
import { formatDOB } from '../../lib/datetime'
import PatientEntry from '../Common/PatientEntry'
import DiseasesTags from '../Common/DiseasesTags'
import CircleCheckIcon from '../Common/CircleCheckIcon'

// Width of antd's expand-icon column. The expanded details are padded by the
// same amount so their columns line up with the primary card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing or truncating.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#6b6b6b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  ok: {
    color: '#52c41a',
  },
  notOk: {
    color: '#f5222d',
  },
})

function Field({ label, children = null }) {
  const classes = useStyles()
  return (
    <div className={classes.field}>
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
}

// Always-visible summary shown for every patient row on mobile.
export function PatientMobileCard({ patient }) {
  const classes = useStyles()
  const { currentDevice } = patient
  // Line 2: device type · company (empties dropped so there's no dangling separator).
  const deviceLine = [currentDevice?.modelType, currentDevice?.modelManufacturer]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className={classes.card}>
      <div className={classes.name}>
        <PatientEntry patient={patient} />
      </div>
      {deviceLine && <div className={classes.line}>{deviceLine}</div>}
      <div className={classes.line}>
        {formatDOB(patient.birthDate) || '—'} · MRN: {patient.practiceMrn || '—'}
      </div>
    </div>
  )
}

PatientMobileCard.propTypes = {
  patient: PropTypes.shape({
    practiceMrn: PropTypes.string,
    birthDate: PropTypes.string,
    currentDevice: PropTypes.shape({
      modelManufacturer: PropTypes.string,
      modelType: PropTypes.string,
    }),
  }).isRequired,
}

// Fields hidden behind the per-row expand toggle.
export function PatientMobileDetails({ patient, isAdmin = false, isSearching = false }) {
  const classes = useStyles()
  const theme = useTheme()
  const { currentDevice } = patient

  const patientSummary = {
    firstName: patient.firstName,
    lastName: patient.lastName,
    atrialFibrilationFlutterDisease: patient.atrialFibrilationFlutterDisease,
    anticoagulatedDisease: patient.anticoagulatedDisease,
  }

  const serials =
    isAdmin && isSearching
      ? (patient.devices || []).map(d => d.serialNumber).filter(Boolean)
      : [patient.devices?.find(d => d.current)?.serialNumber].filter(Boolean)

  let icmActive = '—'
  if (currentDevice?.icmCapable) {
    icmActive = (
      <CircleCheckIcon
        checked={currentDevice?.icmActive}
        trueClassName={classes.ok}
        falseClassName={classes.notOk}
      />
    )
  }

  return (
    <div className={classes.detailsGrid}>
      <Field label="Stroke prevention">
        <DiseasesTags patientSummary={patientSummary} />
      </Field>
      <Field label="Warnings">
        {patient.hasDeviceWarnings ? (
          <span style={{ color: theme.brandDangerMid }}>
            <WarningOutlined /> {patient.deviceWarnings?.join(', ')}
          </span>
        ) : (
          'None'
        )}
      </Field>
      <Field label="ICM active">{icmActive}</Field>
      <Field label="Serial number">
        {serials.length ? serials.map(s => <div key={s}>{s}</div>) : '—'}
      </Field>
      {isAdmin && (
        <Field label="Enrolled">
          <CircleCheckIcon checked={patient.enrolled} />
        </Field>
      )}
      {isAdmin && (
        <Field label="Paired">
          <CircleCheckIcon checked={patient.paired} />
        </Field>
      )}
    </div>
  )
}

PatientMobileDetails.propTypes = {
  patient: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    atrialFibrilationFlutterDisease: PropTypes.bool,
    anticoagulatedDisease: PropTypes.bool,
    hasDeviceWarnings: PropTypes.bool,
    deviceWarnings: PropTypes.arrayOf(PropTypes.string),
    enrolled: PropTypes.bool,
    paired: PropTypes.bool,
    devices: PropTypes.arrayOf(PropTypes.shape({})),
    currentDevice: PropTypes.shape({
      icmCapable: PropTypes.bool,
      icmActive: PropTypes.bool,
    }),
  }).isRequired,
  isAdmin: PropTypes.bool,
  isSearching: PropTypes.bool,
}
